import type { LandingPromoRuntime } from '@entities/LandingPage'

import type { LandingBlockProps } from '@features/landing-page-builder/model/blockRegistry'

import styles from './LandingPromoCountersBlock.module.scss'

/*
  promo_counters: participant-facing counters (earned/used chances, available
  spins).

  The numbers are NOT read from the payload — each item.code maps to a value in
  landingPage.promo, which the backend computes. The payload only carries labels.
  Rendered only when that runtime is present, i.e. on a promo landing for a
  participant.
*/
interface CounterItem {
  code?: string;
  title?: string;
  label?: string;
  suffix?: string;
}

interface PromoCountersPayload {
  title?: string;
  items?: CounterItem[];
}

const COUNTER_VALUES: Record<string, keyof LandingPromoRuntime> = {
  earnedChances: 'earnedChances',
  usedChances: 'usedChances',
  availableWheelChances: 'availableWheelChances',
  availableSpins: 'availableWheelChances',
}

export default function LandingPromoCountersBlock ({ payload, promo }: LandingBlockProps) {
  const { title, items } = (payload ?? {}) as PromoCountersPayload

  if (!promo) {
    return null
  }

  const counters = (items ?? [])
    .map((item) => {
      const field = item.code ? COUNTER_VALUES[item.code] : undefined
      const value = field ? promo[field] : undefined

      if (typeof value !== 'number') {
        return null
      }

      return {
        key: item.code as string,
        label: item.title || item.label || '',
        value,
        suffix: item.suffix,
      }
    })
    .filter((counter): counter is NonNullable<typeof counter> => Boolean(counter))

  if (counters.length === 0) {
    return null
  }

  return (
    <section className={styles.root}>
      {title && <h2 className={styles.root__title}>{title}</h2>}

      <div className={styles.root__grid}>
        {counters.map((counter) => (
          <div className={styles.counter} key={counter.key}>
            <span className={styles.counter__value}>
              {counter.value}
              {counter.suffix ? <span className={styles.counter__suffix}> {counter.suffix}</span> : null}
            </span>
            {counter.label && <span className={styles.counter__label}>{counter.label}</span>}
          </div>
        ))}
      </div>
    </section>
  )
}
